(() => {
  'use strict';

  /**
   * The storefront's one reader of the market's settings, `GET /api/v1/markets/{market}` of the Checkout API (BEST-284).
   * The Checkout API owns whether the checkout serves the market and the market's support contact (MarketConfiguration,
   * edited in the Platform). The checkout asks before it starts a session, and every support contact on the page marked
   * `data-bestio-market-contact="email|phone"` is filled from the answer. The contact Liquid renders from the theme's
   * settings is only the fallback for a page whose API does not answer and for a market the checkout does not serve.
   *
   * The market and the API origin come from the config element `bestio-market-settings.liquid` renders once per page.
   */
  // Short: the checkout waits for the answer before it starts, and without one the server decides at the session start.
  const REQUEST_TIMEOUT_MS = 4000;
  // The settings change rarely; the answer is kept for five minutes per tab, so browsing does not ask on every page and
  // does not use up the rate limit. A failed answer is never kept.
  const CACHE_TTL_MS = 5 * 60 * 1000;
  const CACHE_KEY_PREFIX = 'bestio:market-settings:v1:';

  /**
   * The answer as the theme uses it, or null when the API did not answer. A contact is null when the theme should keep its
   * own (the market is unknown to the API) and '' when the market has none. A market the API does not know (404
   * market_not_found) does not run the checkout; any other 404 is a Checkout API without this route yet (a theme deployed
   * before the hub), which is no answer: the server then decides at the session start.
   */
  function normalize(status, body) {
    if (status === 404 && body?.code === 'market_not_found') {
      return Object.freeze({checkoutEnabled: false, supportEmail: null, supportPhone: null, heurekaSurveyOptOutOffered: false});
    }
    if (status < 200 || status >= 300 || !body || typeof body !== 'object') return null;
    const text = value => (typeof value === 'string' ? value.trim() : null);
    return Object.freeze({
      checkoutEnabled: body.checkoutEnabled === true,
      supportEmail: text(body.supportEmail),
      supportPhone: text(body.supportPhone),
      // The details step offers the refusal of Heureka's questionnaire only where the API says so (ADR 0021).
      heurekaSurveyOptOutOffered: body.heurekaSurveyOptOutOffered === true,
    });
  }

  /** Fills one marked contact; an empty contact of the market hides its item, an unknown one leaves the theme's. */
  function applyContact(element, settings) {
    const kind = element.dataset.bestioMarketContact;
    const value = kind === 'phone' ? settings?.supportPhone : kind === 'email' ? settings?.supportEmail : null;
    if (value == null) return;
    const item = element.closest?.('[data-bestio-market-contact-item]') || element;
    item.hidden = value === '';
    if (value === '') return;
    element.textContent = value;
    element.setAttribute('href', kind === 'phone' ? `tel:${value.replace(/\s+/g, '')}` : `mailto:${value}`);
  }

  const api = {normalize, applyContact};

  const config = document.querySelector('[data-bestio-market-settings]');
  const settings = {
    apiBaseUrl: String(config?.dataset.checkoutApiBaseUrl || '').replace(/\/$/, ''),
    marketCode: String(config?.dataset.marketCountryCode || 'CZ').toUpperCase(),
  };
  const cacheKey = `${CACHE_KEY_PREFIX}${settings.apiBaseUrl}|${settings.marketCode}`;
  function cached(now = Date.now()) {
    try {
      const entry = JSON.parse(window.sessionStorage.getItem(cacheKey) || 'null');
      if (entry && entry.settings && now - entry.storedAt >= 0 && now - entry.storedAt < CACHE_TTL_MS) return Object.freeze(entry.settings);
    } catch (_) { /* no storage or a broken entry: ask the API */ }
    return null;
  }
  function remember(value) {
    try { window.sessionStorage.setItem(cacheKey, JSON.stringify({storedAt: Date.now(), settings: value})); } catch (_) { /* storage full or blocked */ }
  }

  let request;
  /** The market's settings; one request per page at most and none while the tab keeps a fresh answer, null when the API
   *  does not answer (the checkout then lets the server decide at the session start). */
  function load() {
    if (request) return request;
    if (!settings.apiBaseUrl) {
      request = Promise.resolve(null);
      return request;
    }
    const kept = cached();
    if (kept) {
      request = Promise.resolve(kept);
      return request;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    request = fetch(`${settings.apiBaseUrl}/api/v1/markets/${encodeURIComponent(settings.marketCode)}`,
      {headers: {Accept: 'application/json'}, signal: controller.signal})
      .then(async response => normalize(response.status, await response.json().catch(() => null)))
      .then(value => {
        if (!value) throw new Error('The market settings answer is not usable.');
        remember(value);
        return value;
      })
      .catch(error => {
        console.warn('Bestio market settings are unavailable; the theme keeps its own contact.', {marketCode: settings.marketCode, reason: error?.message});
        return null;
      })
      .finally(() => window.clearTimeout(timer));
    return request;
  }

  window.BestioMarketSettings = Object.freeze({...api, load});

  if (!config) return;
  const contacts = document.querySelectorAll('[data-bestio-market-contact]');
  if (!contacts.length) return;
  load().then(value => { if (value) contacts.forEach(element => applyContact(element, value)); });
})();
